/* sugar:assets/gift-unlocks.js@2.1.7 */
/*
 * gift-unlocks — a row of gifts that unlock as the shopper raises quantity or picks a variant.
 *
 * A root is any [data-gift-unlocks]; its data-gift-unlocks-config says which product it gates on
 * and how to print money. The quantity comes from the section's price store (the same number the
 * price, subtotal and buy button show), the variant from variant:change.
 *
 * Unlocked gifts ride along with whichever buy button submits, through SugarCart.extras (the
 * root carries data-cart-extras and exposes sugarExtras). Nothing here intercepts a request.
 *
 * Every line this engine adds carries _gift_unlock = 'true' plus the requirement it was earned
 * with, so on every cart change the page can settle up: a gift whose requirement the cart no
 * longer meets comes out, a gift the cart now qualifies for goes in.
 */
(function () {
  'use strict';
  if (window.SugarGiftUnlocks) return;

  const ROOT = '[data-gift-unlocks]';
  const ITEM = '[data-gift-unlock]';
  const PROP = '_gift_unlock';
  const FOR_PROP = '_gift_for_product';
  const QTY_PROP = '_gift_unlock_required_qty';
  const VALUE_PROP = '_gift_unlock_variant_value';
  // Lines the theme adds on the shopper's behalf never count toward a threshold
  const AUTO_PROPS = [PROP, '_gift_list', '_gift_picker', '_offer_gift', '_sugar_progress_goal', '_cart_toggle', '_cart_upsell'];

  const int = (value, fallback = 0) => {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  };
  const norm = (value) => String(value == null ? '' : value).toLowerCase().trim();

  // The values an "Unlock on variant" entry names, several split by "|"
  function choices(value) {
    return String(value == null ? '' : value).split('|').map(norm).filter(Boolean);
  }

  function isTrue(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  function isAutoLine(item) {
    const p = item && item.properties;
    if (!p) return false;
    return AUTO_PROPS.some((key) => {
      const v = p[key];
      return v !== undefined && v !== null && v !== '' && v !== false;
    });
  }

  function isCart(value) {
    return !!(value && Array.isArray(value.items) && typeof value.item_count === 'number');
  }

  function cartOf(detail) {
    if (isCart(detail)) return detail;
    if (detail && isCart(detail.cart)) return detail.cart;
    if (detail && isCart(detail.cartData)) return detail.cartData;
    return null;
  }

  function roundCents(cents, rounding) {
    if (!rounding || rounding === 'none') return cents;
    const dollars = Math.floor(cents / 100);
    switch (rounding) {
      case '95':
        return dollars * 100 + 95;
      case '99':
        return dollars * 100 + 99;
      case '00':
        return dollars * 100;
      default:
        return cents;
    }
  }

  function formatMoney(cents, format) {
    if (window.SugarPricing && typeof window.SugarPricing.formatMoney === 'function') {
      return window.SugarPricing.formatMoney(cents, format);
    }
    return (format || '${{amount}}').replace(/\{\{\s*amount\w*\s*\}\}/, (int(cents) / 100).toFixed(2));
  }

  function sectionIds() {
    if (typeof window.sugarCartSectionIds === 'function') return window.sugarCartSectionIds();
    return ['sugar-cart-drawer', 'cart-icon-bubble'];
  }

  async function postAdd(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds(), sections_url: window.location.pathname }),
    });
    return res.json();
  }

  async function postUpdate(updates) {
    const payload = { updates, sections: sectionIds(), sections_url: window.location.pathname };
    if (window.SugarCart && typeof window.SugarCart.update === 'function') return window.SugarCart.update(payload);
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  }

  async function announce(result) {
    const cartData = window.SugarCart ? await window.SugarCart.get({ fresh: true }) : cartOf(result);
    if (cartData && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-unlocks', cartData, sections: result && result.sections });
    }
    document.dispatchEvent(new CustomEvent('cart:update', { detail: result }));
    return cartData;
  }

  const roots = new Set();

  class GiftUnlocks {
    constructor(root) {
      this.root = root;
      root.sugarGiftUnlocks = this;
      try {
        this.cfg = JSON.parse(root.dataset.giftUnlocksConfig || '{}');
      } catch (e) {
        this.cfg = {};
      }
      this.cfg.productId = String(this.cfg.productId || '');
      this.quantity = 1;
      this.options = Array.isArray(this.cfg.options) ? this.cfg.options.map(norm) : [];
      this.handlers = [];
      this.destroyed = false;
      this.init();
    }

    /* ------------------------------------------------------------ setup */

    init() {
      roots.add(this);
      this.mirror();
      this.paintPrices();
      this.root.setAttribute('data-cart-extras', '');
      this.root.sugarExtras = {
        collect: () => this.collect(),
        commit: () => refresh(),
      };
      this.bind();
      this.paint();
      this.setupAnalytics();
    }

    /*
     * No gifts of its own: show the gifts of another Gift Unlocks in the section (the step-1 panel,
     * for the copy inside a Step 2), so the gift products are picked in one place. The copies carry
     * the same lines, and the cart helper hands identical lines over once.
     */
    mirror() {
      if (this.items().length) return;
      const section = this.sectionEl();
      if (!section) return;
      const source = Array.from(section.querySelectorAll(ROOT)).find((root) => root !== this.root && root.querySelector(ITEM));
      const list = source && source.querySelector('.sugar-gift-unlocks__items');
      if (!list) return;
      const copy = list.cloneNode(true);
      copy.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
      const empty = this.root.querySelector('.sugar-gift-unlocks__empty');
      if (empty) empty.replaceWith(copy);
      else this.root.appendChild(copy);
      this.root.setAttribute('data-gift-unlocks-mirror', '');
    }

    destroy() {
      this.destroyed = true;
      roots.delete(this);
      this.handlers.forEach(([target, type, fn, opts]) => target.removeEventListener(type, fn, opts));
      this.handlers = [];
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
      if (this.unlockWatcher) this.unlockWatcher.disconnect();
      delete this.root.sugarGiftUnlocks;
      delete this.root.sugarExtras;
    }

    listen(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.handlers.push([target, type, fn, opts]);
    }

    sameSection(detail) {
      return !detail || !detail.sectionId || String(detail.sectionId) === String(this.cfg.sectionId);
    }

    sectionEl() {
      return document.getElementById('shopify-section-' + this.cfg.sectionId) || this.root.closest('.shopify-section');
    }

    bind() {
      this.listen(document, 'variant:change', (e) => {
        const detail = e.detail || {};
        const variant = detail.variant;
        if (!variant || !this.sameSection(detail)) return;
        if (Array.isArray(variant.options)) this.options = variant.options.map(norm);
        this.paint();
      });
      this.catchUpVariant();

      /*
       * The section's price store already owns the quantity: it hears the offers block, the
       * quantity stepper, the buy button's own picker and a bundle builder, and settles them in
       * one place. Subscribing to it means a gift unlocks on exactly the number the price shows.
       */
      const store = window.SugarPricing && typeof window.SugarPricing.store === 'function'
        ? window.SugarPricing.store({
            sectionId: this.cfg.sectionId,
            productId: this.cfg.productId,
            variantIds: this.cfg.variantIds || [],
            price: int(this.cfg.price),
            compareAt: int(this.cfg.compareAt),
            available: this.cfg.available !== false,
            format: this.cfg.moneyFormat,
          })
        : null;
      if (store) {
        this.unsubscribe = store.subscribe((state) => this.setQuantity(state.quantity));
        return;
      }

      // No store on the page: hear the two quantity sources directly
      this.listen(document, 'offers:quantity-changed', (e) => {
        const detail = e.detail || {};
        if (!this.sameSection(detail) || !detail.quantity) return;
        this.setQuantity(detail.quantity);
      });
      this.listen(document, 'change', (e) => {
        const t = e.target;
        if (!t || t.name !== 'quantity') return;
        const section = this.sectionEl();
        if (section && !section.contains(t)) return;
        this.setQuantity(t.value);
      });
      const section = this.sectionEl();
      const selected = section && section.querySelector('.offer.is-selected[data-quantity]');
      const field = section && section.querySelector('[name="quantity"]');
      if (selected) this.setQuantity(selected.dataset.quantity);
      else if (field) this.setQuantity(field.value);
    }

    /*
     * An Offer List that booted first announced its preselected tier's variant before this
     * listened, so read that variant from it (the tier's first slot, else the tier's own).
     */
    catchUpVariant() {
      const section = this.sectionEl();
      if (!section) return;
      const controller = Array.from(section.querySelectorAll('[data-sugar-offers]'))
        .map((root) => root.sugarOffers)
        .find((c) => c && c.selectedId && typeof c.getVariantById === 'function' && String(c.sectionId) === String(this.cfg.sectionId));
      if (!controller) return;
      const offer = typeof controller.getOfferEl === 'function' ? controller.getOfferEl(controller.selectedId) : null;
      const picks = controller.selectedVariants && controller.selectedVariants[controller.selectedId];
      const variant = controller.getVariantById((picks && picks[1]) || (offer && offer.dataset.variantId));
      if (variant && Array.isArray(variant.options)) this.options = variant.options.map(norm);
    }

    setQuantity(next) {
      const qty = Math.max(1, int(next, 1));
      if (qty === this.quantity) return;
      this.quantity = qty;
      this.paint();
    }

    /* ------------------------------------------------------------ state */

    items() {
      return Array.from(this.root.querySelectorAll(ITEM));
    }

    // "3 Bottles | 5 Bottles" unlocks on either value.
    variantMatches(rawValue) {
      const title = this.options.join(' / ');
      return choices(rawValue).some((target) => this.options.indexOf(target) !== -1 || title === target);
    }

    isUnlocked(item) {
      if (item.dataset.unlockMode === 'variant') return this.variantMatches(item.dataset.unlockValue);
      return this.quantity >= Math.max(1, int(item.dataset.requiredQuantity, 1));
    }

    paint() {
      if (this.destroyed) return;
      this.items().forEach((item) => {
        const on = this.isUnlocked(item);
        const next = on ? 'true' : 'false';
        if (item.getAttribute('data-unlocked') !== next) item.setAttribute('data-unlocked', next);
      });
      this.paintTotal();
    }

    paintTotal() {
      const line = this.root.querySelector('[data-gift-unlocks-total-line]');
      const totalEl = this.root.querySelector('[data-gift-unlocks-total]');
      if (!line || !totalEl) return;
      const unlockedOnly = this.cfg.totalMode === 'unlocked';
      let total = 0;
      let counted = 0;
      this.items().forEach((item) => {
        if (unlockedOnly && !this.isUnlocked(item)) return;
        total += int(item.dataset.giftPrice);
        counted += 1;
      });
      // Nothing unlocked yet would read "$0.00 FREE", so the line hides instead
      if (unlockedOnly && counted === 0) {
        line.style.display = 'none';
        return;
      }
      line.style.display = '';
      totalEl.textContent = formatMoney(roundCents(total, this.cfg.rounding), this.cfg.moneyFormat);
    }

    // The struck-through prices: the child renders them with the theme's currency default,
    // and the parent's rounding and currency choice are applied here.
    paintPrices() {
      const format = this.cfg.moneyFormat;
      const rounding = this.cfg.rounding;
      this.items().forEach((item) => {
        // A perk with no gift product keeps the price text its block printed.
        if (item.hasAttribute('data-gift-display')) return;
        const price = formatMoney(roundCents(int(item.dataset.giftPrice), rounding), format);
        const template = item.dataset.priceTemplate || '[price]';
        item.querySelectorAll('[data-gift-price-text]').forEach((el) => {
          el.innerHTML = template.replace(/\[price\]/g, price);
        });
        const locked = item.dataset.lockedTemplate || '';
        if (locked) {
          item.querySelectorAll('[data-gift-locked-text]').forEach((el) => {
            el.innerHTML = locked.replace(/\[price\]/g, price);
          });
        }
      });
    }

    /* ------------------------------------------------------------ lines */

    // The requirement a gift is earned with, stored on its cart line so a later cart change can
    // check it even when the shopper is no longer on this page.
    lineFor(item) {
      const id = int(item.dataset.variantId);
      if (!id || !this.cfg.productId) return null;
      const properties = { [PROP]: 'true', [FOR_PROP]: this.cfg.productId };
      if (item.dataset.unlockMode === 'variant') {
        const value = norm(item.dataset.unlockValue);
        if (value) properties[VALUE_PROP] = value;
      } else {
        const required = int(item.dataset.requiredQuantity);
        if (required > 0) properties[QTY_PROP] = String(required);
      }
      return { id, quantity: 1, properties };
    }

    // The unlocked gifts, minus any the cart already holds for this product
    collect() {
      const cart = window.SugarCart ? window.SugarCart.peek() : null;
      const inCart = new Set();
      if (cart && Array.isArray(cart.items)) {
        cart.items.forEach((line) => {
          const p = line.properties || {};
          if (isTrue(p[PROP]) && String(p[FOR_PROP] || '') === this.cfg.productId) inCart.add(String(line.variant_id));
        });
      }
      const lines = [];
      this.items().forEach((item) => {
        if (!this.isUnlocked(item)) return;
        if (inCart.has(String(item.dataset.variantId))) return;
        const line = this.lineFor(item);
        if (line) lines.push(line);
      });
      return lines;
    }

    // Every requirement this root knows, keyed product:variant, for settling the cart
    requirements(into) {
      if (!this.cfg.productId) return into;
      this.items().forEach((item) => {
        const variantId = int(item.dataset.variantId);
        if (!variantId) return;
        const key = this.cfg.productId + ':' + variantId;
        if (item.dataset.unlockMode === 'variant') {
          const value = norm(item.dataset.unlockValue);
          if (!value) return;
          if (!into.variant.has(key)) into.variant.set(key, { productId: this.cfg.productId, variantId, value });
          return;
        }
        const required = int(item.dataset.requiredQuantity);
        if (!(required > 0)) return;
        const existing = into.quantity.get(key);
        if (!Number.isFinite(existing) || required < existing) into.quantity.set(key, required);
      });
      return into;
    }

    /* ------------------------------------------------------------ analytics */

    context() {
      const d = this.root.dataset;
      return {
        template: d.sugarTemplate || '',
        path: window.location.pathname,
        product: d.pageProductHandle || '',
        productTitle: d.sugarProductTitle || '',
        label: '',
        section: this.cfg.sectionId || '',
      };
    }

    track(type, detail) {
      const entity = this.root.dataset.sugarEntity;
      if (!entity) return;
      const args = [type, 'gift_bar', entity, detail || '', this.context()];
      if (window.SugarAnalytics) window.SugarAnalytics.track.apply(null, args);
      else (window._sugarQ = window._sugarQ || []).push(args);
    }

    setupAnalytics() {
      const root = this.root;
      const entity = root.dataset.sugarEntity;
      if (!entity) return;
      const ctx = this.context();
      if (window.SugarAnalytics) {
        window.SugarAnalytics.observe(root, entity, 'gift_bar', ctx);
      } else {
        document.addEventListener(
          'sugar:analytics:ready',
          () => {
            if (window.SugarAnalytics) window.SugarAnalytics.observe(root, entity, 'gift_bar', ctx);
          },
          { once: true }
        );
      }
      // A tier unlocking for the first time this session is a conversion
      this.unlockWatcher = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName !== 'data-unlocked' || mutation.oldValue !== 'false') continue;
          const el = mutation.target;
          if (el.getAttribute('data-unlocked') !== 'true') continue;
          const tier = this.items().indexOf(el) + 1;
          if (tier < 1) continue;
          let first = true;
          try {
            const key = '_sugar_gift_' + entity + '_' + tier;
            if (sessionStorage.getItem(key)) first = false;
            else sessionStorage.setItem(key, '1');
          } catch (e) {
            /* private mode */
          }
          if (first) this.track('convert', 'unlocked:' + tier);
        }
      });
      this.unlockWatcher.observe(root, { attributes: true, attributeFilter: ['data-unlocked'], attributeOldValue: true, subtree: true });
    }
  }

  /* ------------------------------------------------------------ settling the cart */

  let busy = false;
  let dirty = false;
  let timer = null;

  function queueReconcile(cart) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      reconcile(cart).catch((e) => console.error('[Gift Unlocks] Sync error:', e));
    }, 40);
  }

  async function refresh() {
    if (!window.SugarCart || !roots.size) return;
    try {
      const cart = await window.SugarCart.get({ fresh: true });
      queueReconcile(cart);
    } catch (e) {
      /* the next cart event tries again */
    }
  }

  /*
   * Read the cart once and settle every gift line against what the page requires. Lines whose
   * requirement the cart no longer meets come out; requirements the cart now meets and holds no
   * gift for go in. One update and one add at most, then one announcement for the drawer.
   */
  async function reconcile(cart) {
    if (!roots.size) return;
    if (busy) {
      dirty = true;
      return;
    }
    if (!isCart(cart)) {
      cart = window.SugarCart ? await window.SugarCart.get({ fresh: true }) : null;
      if (!isCart(cart)) return;
    }

    // What the shopper is buying, per product: units and the variant values in play
    const quantities = new Map();
    const tokens = new Map();
    cart.items.forEach((item) => {
      if (isAutoLine(item)) return;
      const productId = String(item.product_id);
      quantities.set(productId, (quantities.get(productId) || 0) + int(item.quantity));
      let set = tokens.get(productId);
      if (!set) {
        set = new Set();
        tokens.set(productId, set);
      }
      const title = norm(item.variant_title);
      if (title) {
        set.add(title);
        title.split(' / ').forEach((part) => {
          const token = part.trim();
          if (token) set.add(token);
        });
      }
    });
    const hasVariant = (productId, value) => {
      const set = tokens.get(String(productId));
      return !!(set && choices(value).some((choice) => set.has(choice)));
    };

    const required = { quantity: new Map(), variant: new Map() };
    roots.forEach((root) => root.requirements(required));

    const orphans = [];
    const held = new Map();
    cart.items.forEach((item) => {
      const p = item.properties || {};
      if (!isTrue(p[PROP])) return;
      const productId = String(p[FOR_PROP] || '').trim();
      const variantId = int(item.variant_id);
      const key = productId && variantId ? productId + ':' + variantId : null;
      const qualifyingQty = quantities.get(productId) || 0;
      const orphaned = !productId || qualifyingQty <= 0;

      // A line answers for how it was earned. One without a stored requirement (added by an
      // older page) is judged by what this page requires for that gift.
      const lineValue = norm(p[VALUE_PROP]);
      const lineQty = int(p[QTY_PROP]);
      const pageVariant = key ? required.variant.get(key) : null;
      const pageQty = key ? required.quantity.get(key) : null;
      let keep = !orphaned;
      if (keep) {
        if (lineValue) keep = hasVariant(productId, lineValue);
        else if (lineQty > 0) keep = qualifyingQty >= lineQty;
        else if (pageVariant) keep = hasVariant(productId, pageVariant.value);
        else if (pageQty > 0) keep = qualifyingQty >= pageQty;
      }
      if (!keep) {
        orphans.push(item.key);
        return;
      }
      if (key) held.set(key, (held.get(key) || 0) + 1);
    });

    const additions = [];
    required.quantity.forEach((need, key) => {
      const [productId, variantRaw] = key.split(':');
      const variantId = int(variantRaw);
      if (!productId || !variantId) return;
      if ((quantities.get(productId) || 0) < need) return;
      if (held.get(key) > 0) return;
      additions.push({ id: variantId, quantity: 1, properties: { [PROP]: 'true', [FOR_PROP]: productId, [QTY_PROP]: String(need) } });
    });
    required.variant.forEach((req, key) => {
      if (!hasVariant(req.productId, req.value)) return;
      if (held.get(key) > 0) return;
      additions.push({ id: req.variantId, quantity: 1, properties: { [PROP]: 'true', [FOR_PROP]: req.productId, [VALUE_PROP]: req.value } });
    });

    window.SugarGiftUnlocks.last = { orphans: orphans.slice(), additions: additions.slice(), quantities: Array.from(quantities) };
    if (!orphans.length && !additions.length) return;

    busy = true;
    try {
      let result = null;
      if (orphans.length) {
        const updates = {};
        orphans.forEach((key) => {
          updates[key] = 0;
        });
        result = await postUpdate(updates);
        if (result && result.status) console.error('[Gift Unlocks] Cart error:', result.description);
      }
      if (additions.length) {
        result = await postAdd(additions);
        if (result && result.status) console.error('[Gift Unlocks] Cart error:', result.description);
      }
      await announce(result);
    } finally {
      busy = false;
      if (dirty) {
        dirty = false;
        queueReconcile(null);
      }
    }
  }

  /* ------------------------------------------------------------ boot */

  function init(scope) {
    const host = scope && scope.nodeType === 1 ? scope : document;
    const found = host.matches && host.matches(ROOT) ? [host] : Array.from(host.querySelectorAll(ROOT));
    found.forEach((root) => {
      if (!root.sugarGiftUnlocks) new GiftUnlocks(root);
    });
  }

  function teardown(scope) {
    const host = scope && scope.nodeType === 1 ? scope : null;
    if (!host) return;
    const found = host.matches(ROOT) ? [host] : Array.from(host.querySelectorAll(ROOT));
    found.forEach((root) => root.sugarGiftUnlocks && root.sugarGiftUnlocks.destroy());
  }

  function start() {
    init();
    // A shopper who comes back to the page may hold gifts the cart no longer earns
    refresh();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('cart:update', (e) => queueReconcile(cartOf(e.detail)));
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => queueReconcile(cartOf(event)));
  }

  // Roots that arrive later (a section re-rendered, a block added in the editor) and roots that
  // leave are picked up here.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.removedNodes) {
        if (node.nodeType === 1) teardown(node);
      }
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1 && (node.matches(ROOT) || node.querySelector(ROOT))) init(node);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.SugarGiftUnlocks = {
    init,
    refresh,
    get(root) {
      return root && root.sugarGiftUnlocks ? root.sugarGiftUnlocks : null;
    },
  };
})();
